/**
 * Dream Logistics 애플리케이션 상수 정의
 */

// ============================================================================
// API 설정
// ============================================================================

export const API_CONFIG = {
  BASE_URL: import.meta.env.VITE_API_URL || 'http://localhost:5714/api',
  TIMEOUT: 30000, // 30초
  RETRY_ATTEMPTS: 3,
} as const;

// ============================================================================
// 페이지네이션 설정
// ============================================================================

export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_PAGE_SIZE: 20,
  PAGE_SIZE_OPTIONS: [10, 20, 50, 100] as const,
  MAX_PAGE_SIZE: 100,
} as const;

// ============================================================================
// 날짜/시간 포맷
// ============================================================================

export const DATE_FORMATS = {
  DATE: 'yyyy-MM-dd', // 2025-11-04
  TIME: 'HH:mm', // 14:30
  DATETIME: 'yyyy-MM-dd HH:mm', // 2025-11-04 14:30
  DATETIME_FULL: 'yyyy-MM-dd HH:mm:ss', // 2025-11-04 14:30:25
  DATE_KR: 'yyyy년 MM월 dd일', // 2025년 11월 04일
  DATETIME_KR: 'yyyy년 MM월 dd일 HH:mm', // 2025년 11월 04일 14:30
} as const;

// ============================================================================
// 배차 관리 (Dispatch) 상태
// ============================================================================

export const DISPATCH_STATUS = {
  PENDING: 'pending', // 대기
  ASSIGNED: 'assigned', // 할당됨
  IN_PROGRESS: 'in_progress', // 진행중
  COMPLETED: 'completed', // 완료
  CANCELLED: 'cancelled', // 취소
} as const;

export const DISPATCH_STATUS_LABELS: Record<keyof typeof DISPATCH_STATUS, string> = {
  PENDING: '대기',
  ASSIGNED: '할당됨',
  IN_PROGRESS: '진행중',
  COMPLETED: '완료',
  CANCELLED: '취소',
};

// ============================================================================
// 픽업 관리 (Pickup) 상태
// ============================================================================

export const PICKUP_STATUS = {
  SCHEDULED: 'scheduled', // 예정
  PICKING: 'picking', // 픽업중
  PICKED: 'picked', // 픽업완료
  CANCELLED: 'cancelled', // 취소
} as const;

export const PICKUP_STATUS_LABELS: Record<keyof typeof PICKUP_STATUS, string> = {
  SCHEDULED: '예정',
  PICKING: '픽업중',
  PICKED: '픽업완료',
  CANCELLED: '취소',
};

// ============================================================================
// 배송 관리 (Delivery) 상태
// ============================================================================

export const DELIVERY_STATUS = {
  PENDING: 'pending', // 대기
  IN_TRANSIT: 'in_transit', // 배송중
  DELIVERED: 'delivered', // 배송완료
  FAILED: 'failed', // 실패
  RETURNED: 'returned', // 반품
} as const;

export const DELIVERY_STATUS_LABELS: Record<keyof typeof DELIVERY_STATUS, string> = {
  PENDING: '대기',
  IN_TRANSIT: '배송중',
  DELIVERED: '배송완료',
  FAILED: '실패',
  RETURNED: '반품',
};

// ============================================================================
// 전송 관리 (Transfer) 상태
// ============================================================================

export const TRANSFER_STATUS = {
  PENDING: 'pending', // 대기
  PROCESSING: 'processing', // 처리중
  COMPLETED: 'completed', // 완료
  FAILED: 'failed', // 실패
} as const;

export const TRANSFER_STATUS_LABELS: Record<keyof typeof TRANSFER_STATUS, string> = {
  PENDING: '대기',
  PROCESSING: '처리중',
  COMPLETED: '완료',
  FAILED: '실패',
};

// ============================================================================
// 사용자 역할 (User Roles)
// ============================================================================

export const USER_ROLES = {
  ADMIN: 'admin', // 관리자
  MANAGER: 'manager', // 매니저
  OPERATOR: 'operator', // 운영자
} as const;

export const USER_ROLE_LABELS: Record<keyof typeof USER_ROLES, string> = {
  ADMIN: '관리자',
  MANAGER: '매니저',
  OPERATOR: '운영자',
};

// ============================================================================
// 차량 종류 (Vehicle Types)
// ============================================================================

export const VEHICLE_TYPES = {
  ONE_TON: '1ton',
  TWO_TON: '2ton',
  THREE_TON: '3ton',
  FIVE_TON: '5ton',
  TRUCK: 'truck',
  VAN: 'van',
} as const;

export const VEHICLE_TYPE_LABELS: Record<keyof typeof VEHICLE_TYPES, string> = {
  ONE_TON: '1톤',
  TWO_TON: '2톤',
  THREE_TON: '3톤',
  FIVE_TON: '5톤',
  TRUCK: '트럭',
  VAN: '밴',
};

// ============================================================================
// 우선순위 (Priority)
// ============================================================================

export const PRIORITY = {
  LOW: 'low', // 낮음
  MEDIUM: 'medium', // 보통
  HIGH: 'high', // 높음
  URGENT: 'urgent', // 긴급
} as const;

export const PRIORITY_LABELS: Record<keyof typeof PRIORITY, string> = {
  LOW: '낮음',
  MEDIUM: '보통',
  HIGH: '높음',
  URGENT: '긴급',
};

// ============================================================================
// Local Storage Keys
// ============================================================================

export const STORAGE_KEYS = {
  ACCESS_TOKEN: 'dream_main_token',
  REFRESH_TOKEN: 'dream_refresh_token',
  USER: 'dream_user',
  LANGUAGE: 'dream_language',
  THEME: 'dream_theme',
} as const;

// ============================================================================
// 세션 설정
// ============================================================================

export const SESSION = {
  TIMEOUT: 30 * 60 * 1000, // 30분 (밀리초)
  WARNING_TIME: 5 * 60 * 1000, // 5분 전 경고 (밀리초)
  REFRESH_INTERVAL: 10 * 60 * 1000, // 10분마다 토큰 갱신 (밀리초)
} as const;

// ============================================================================
// 공지사항 카테고리 (Notice Category)
// ============================================================================

export const NOTICE_CATEGORY = {
  GENERAL: 'general', // 일반
  URGENT: 'urgent', // 긴급
  MAINTENANCE: 'maintenance', // 점검
  UPDATE: 'update', // 업데이트
} as const;

export const NOTICE_CATEGORY_LABELS: Record<keyof typeof NOTICE_CATEGORY, string> = {
  GENERAL: '일반',
  URGENT: '긴급',
  MAINTENANCE: '점검',
  UPDATE: '업데이트',
};

// ============================================================================
// 앱 플랫폼 (App Platform)
// ============================================================================

export const APP_PLATFORM = {
  IOS: 'ios',
  ANDROID: 'android',
} as const;

export const APP_PLATFORM_LABELS: Record<keyof typeof APP_PLATFORM, string> = {
  IOS: 'iOS',
  ANDROID: 'Android',
};

// ============================================================================
// 푸시 알림 타겟 타입
// ============================================================================

export const PUSH_TARGET_TYPE = {
  ALL: 'all', // 전체
  ROLE: 'role', // 역할별
  USER: 'user', // 사용자별
  REGION: 'region', // 권역별
} as const;

export const PUSH_TARGET_TYPE_LABELS: Record<keyof typeof PUSH_TARGET_TYPE, string> = {
  ALL: '전체',
  ROLE: '역할별',
  USER: '사용자별',
  REGION: '권역별',
};

// ============================================================================
// 에러 메시지
// ============================================================================

export const ERROR_MESSAGES = {
  NETWORK_ERROR: '네트워크 오류가 발생했습니다.',
  UNAUTHORIZED: '인증이 필요합니다. 다시 로그인해주세요.',
  FORBIDDEN: '접근 권한이 없습니다.',
  NOT_FOUND: '요청한 리소스를 찾을 수 없습니다.',
  SERVER_ERROR: '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
  VALIDATION_ERROR: '입력 값을 확인해주세요.',
  TIMEOUT: '요청 시간이 초과되었습니다.',
  UNKNOWN_ERROR: '알 수 없는 오류가 발생했습니다.',
} as const;

// ============================================================================
// 타입 유틸리티
// ============================================================================

export type DispatchStatus = (typeof DISPATCH_STATUS)[keyof typeof DISPATCH_STATUS];
export type PickupStatus = (typeof PICKUP_STATUS)[keyof typeof PICKUP_STATUS];
export type DeliveryStatus = (typeof DELIVERY_STATUS)[keyof typeof DELIVERY_STATUS];
export type TransferStatus = (typeof TRANSFER_STATUS)[keyof typeof TRANSFER_STATUS];
export type UserRole = (typeof USER_ROLES)[keyof typeof USER_ROLES];
export type VehicleType = (typeof VEHICLE_TYPES)[keyof typeof VEHICLE_TYPES];
export type Priority = (typeof PRIORITY)[keyof typeof PRIORITY];
export type NoticeCategory = (typeof NOTICE_CATEGORY)[keyof typeof NOTICE_CATEGORY];
export type AppPlatform = (typeof APP_PLATFORM)[keyof typeof APP_PLATFORM];
export type PushTargetType = (typeof PUSH_TARGET_TYPE)[keyof typeof PUSH_TARGET_TYPE];
